<script setup lang="ts">
import type { Product } from '@/storage/products'
import { URL_IMAGE } from '@/config/constants'
import { getProductName } from '@/utils/product/getProductName'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { formatPrice } from '@/event/locale'
import { ref } from 'vue'
import { getImageClassRounded } from '@/happyTheme/utils/catalogCategory/getImageClassRounded'

const imageSizes = ref<Record<number, { width: number; height: number }>>({})

const props = defineProps<{
    product: Product
}>()

function onImageLoad(categoryId: number, event: Event) {
    const img = event.target as HTMLImageElement

    imageSizes.value[categoryId] = { width: img.naturalWidth, height: img.naturalHeight }
}
</script>

<template>
    <section class="catalog-select-product-happy-no-card">
        <div class="w-full">
            <img
                loading="lazy"
                :src="`${URL_IMAGE}${props.product.image}`"
                :alt="getProductName(props.product)"
                :class="{
                    'object-contain': props.product.hasCleanImg,
                    'object-cover object-center': !props.product.hasCleanImg
                }"
                @load="onImageLoad(props.product.product_id, $event)"
                class="img-happy-no-card"
            />

            <div class="div-description-no-card-happy">
                <p class="text-product-name-no-card-happy">
                    {{ getProductName(props.product) }}
                </p>
                <p class="text-price-product-no-card-happy">
                    {{ formatPrice(props.product.price) }}
                </p>
            </div>

            <p
                class="text-description-no-card-happy"
                v-if="getProductDescription(props.product) !== 'Aucune description'"
            >
                {{ getProductDescription(props.product) }}
            </p>
        </div>

        <div class="px-2 pb-0.5 w-full">
            <div class="div-button-select-product-no-card-full-happy">{{ $t('select') }}</div>
        </div>
    </section>
</template>

<style scoped>
.catalog-select-product-happy-no-card {
    @apply relative w-full h-full flex flex-col items-center justify-between gap-1;
    @apply rounded-lg overflow-hidden;
    /* refactored — ifo-dark/light'ta no-card gerçekten card'sız olsun; olası
       kalıntı bg/border/shadow'u temizle. */
    @apply happy-ifo-dark:bg-transparent happy-ifo-dark:border-0 happy-ifo-dark:shadow-none;
}

.img-happy-no-card {
    @apply w-full h-36;
}

.div-description-no-card-happy {
    @apply w-full flex justify-between  text-sm font-semibold py-2 px-3;
}

.text-product-name-no-card-happy {
    @apply text-wrap;
}

.text-price-product-no-card-happy {
    /* refactored */
    @apply text-nowrap text-t-accent pl-3;
    @apply happy-ifo-dark:text-t-accent;
}

.text-description-no-card-happy {
    @apply text-wrap text-xs mt-2 px-3 truncate line-clamp-2;
}

.div-button-select-product-no-card-full-happy {
    @apply flex justify-center items-center w-full cursor-pointer h-8 text-sm;
    @apply rounded-lg mt-1 cursor-pointer font-bold shadow-sm;
    /* refactored */
    @apply bg-t-accent text-t-on-accent shadow-t-border;
    /* refactored: ifo-dark/light pill style — dark-transparan bg + amber
       border + amber text. Ekle butonunun dark-pill tarzına uyumlu. */
    @apply happy-ifo-dark:text-t-accent
           happy-ifo-dark:bg-t-accent/10
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:shadow-none
           happy-ifo-dark:rounded-full
           happy-ifo-dark:tracking-wide;
}

@media (hover: hover) {
    .catalog-select-product-happy-no-card:hover {
        /* refactored */
        @apply bg-t-accent-hover text-t-on-accent;
        /* refactored — ifo-dark/light: hover'da card/glow oluşmasın; sadece
           yazı renkleri amber'e dönsün. Gerçekten card'sız deneyim. */
        @apply happy-ifo-dark:bg-transparent happy-ifo-dark:shadow-none happy-ifo-dark:border-0 happy-ifo-dark:text-t-accent;
    }
    .catalog-select-product-happy-no-card:hover .text-price-product-no-card-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }
    .catalog-select-product-happy-no-card:hover
        .div-button-select-product-no-card-full-happy {
        /* refactored */
        @apply bg-t-accent text-t-on-accent;
        /* refactored: ifo-dark pill'i korusun — hover'da parlak pill hissi. */
        @apply happy-ifo-dark:bg-t-accent/20
               happy-ifo-dark:text-t-accent
               happy-ifo-dark:border-t-accent/60;
    }
}
</style>
